/**
 * Utilitário centralizado para tratamento de erros
 */

/**
 * Tipos de erro conhecidos
 */
export const ErrorTypes = {
  NETWORK: 'NETWORK_ERROR',
  VALIDATION: 'VALIDATION_ERROR',
  AUTHENTICATION: 'AUTHENTICATION_ERROR',
  AUTHORIZATION: 'AUTHORIZATION_ERROR',
  NOT_FOUND: 'NOT_FOUND',
  SERVER: 'SERVER_ERROR',
  UNKNOWN: 'UNKNOWN_ERROR',
};

/**
 * Classifica o tipo de erro baseado na resposta HTTP
 * @param {Error} error - Erro a ser classificado
 * @returns {string} Tipo do erro
 */
export const classifyError = (error) => {
  if (!error.response) {
    // Erro de rede (sem resposta do servidor)
    if (error.request) {
      return ErrorTypes.NETWORK;
    }
    return ErrorTypes.UNKNOWN;
  }

  const { status } = error.response;

  switch (status) {
    case 400:
      return ErrorTypes.VALIDATION;
    case 401:
      return ErrorTypes.AUTHENTICATION;
    case 403:
      return ErrorTypes.AUTHORIZATION;
    case 404:
      return ErrorTypes.NOT_FOUND;
    case 500:
    case 502:
    case 503:
    case 504:
      return ErrorTypes.SERVER;
    default:
      return ErrorTypes.UNKNOWN;
  }
};

/**
 * Extrai mensagem de erro amigável
 * @param {Error} error - Erro a ser processado
 * @returns {string} Mensagem de erro amigável
 */
export const getErrorMessage = (error) => {
  if (!error) {
    return 'Ocorreu um erro desconhecido';
  }

  // Se já for uma string, retorna direto
  if (typeof error === 'string') {
    return error;
  }

  // Se tiver response do axios
  if (error.response) {
    const { data, status } = error.response;

    // Mensagens customizadas do backend
    if (data?.message) {
      return data.message;
    }

    if (data?.error) {
      return data.error;
    }

    // Mensagens padrão por status
    switch (status) {
      case 400:
        return 'Dados inválidos. Verifique as informações e tente novamente.';
      case 401:
        return 'Sessão expirada. Por favor, faça login novamente.';
      case 403:
        return 'Você não tem permissão para realizar esta ação.';
      case 404:
        return 'Recurso não encontrado.';
      case 500:
        return 'Erro interno do servidor. Tente novamente mais tarde.';
      default:
        return `Erro ${status}: ${error.message || 'Erro desconhecido'}`;
    }
  }

  // Erro de rede
  if (error.request) {
    return 'Erro de conexão. Verifique sua internet e tente novamente.';
  }

  // Erro genérico
  return error.message || 'Ocorreu um erro inesperado';
};

/**
 * Extrai detalhes de validação do erro
 * @param {Error} error - Erro a ser processado
 * @returns {Array} Array de erros de validação
 */
export const getValidationErrors = (error) => {
  if (!error.response?.data?.validationErrors) {
    return [];
  }

  return error.response.data.validationErrors.map((err) => ({
    field: err.field || 'unknown',
    message: err.message || 'Erro de validação',
    code: err.code || 'invalid',
  }));
};

/**
 * Verifica se o erro é recuperável (pode tentar novamente)
 * @param {Error} error - Erro a ser verificado
 * @returns {boolean} true se o erro for recuperável
 */
export const isRecoverableError = (error) => {
  const errorType = classifyError(error);
  
  return [
    ErrorTypes.NETWORK,
    ErrorTypes.SERVER,
  ].includes(errorType);
};

/**
 * Loga o erro para monitoramento (pode ser integrado com Sentry, LogRocket, etc)
 * @param {Error} error - Erro a ser logado
 * @param {Object} context - Contexto adicional do erro
 */
export const logError = (error, context = {}) => {
  const errorInfo = {
    message: getErrorMessage(error),
    type: classifyError(error),
    stack: error.stack,
    context,
    timestamp: new Date().toISOString(),
  };

  // Em produção, enviar para serviço de monitoramento
  if (process.env.NODE_ENV === 'production') {
    // Exemplo: Sentry.captureException(error, { extra: context });
    console.error('Erro capturado:', errorInfo);
  } else {
    // Em desenvolvimento, apenas logar no console
    console.error('Erro capturado:', errorInfo);
  }
};

/**
 * Cria um objeto de erro padronizado
 * @param {Error} error - Erro original
 * @param {Object} context - Contexto adicional
 * @returns {Object} Erro padronizado
 */
export const formatError = (error, context = {}) => {
  return {
    type: classifyError(error),
    message: getErrorMessage(error),
    validationErrors: getValidationErrors(error),
    recoverable: isRecoverableError(error),
    original: error,
    context,
  };
};

